import "./StationValue.scss";

// Красивое значение настройки в карточке: плашки вкл/выкл, шкалы громкости и яркости, сроки, бледные пустые значения
type StationValueProps = {
  label: string;
  value: string;
  // Заголовок карточки — по нему понимаем шкалу яркости (LED-панель 0–255)
  card?: string;
};

type Tone = "on" | "off" | "warn" | "bad";

const ON = /^(включ|всегда включ|использу|работает|разрешен|есть|да$|подключ|активн)/i;
const OFF = /^(выключ|отключ|не использу|запрещ|стоит|нет$|неактивн|всегда выключ)/i;
const ADMIN_BLOCK = /блокировка администратора/i;
const EMPTY = /^(—|-|не выбран|не указан|не задан|нет данных|не настроен)/i;

// Шкала: громкость курантов 0–30 (показываем в %), яркость в процентах 0–100 или 0–255 у LED-панели
const scaleOf = (label: string, value: string, card?: string): { value: number; max: number; text: string } | null => {
  const percent = value.match(/^(\d{1,3})\s*%$/);
  const number = value.match(/^(\d{1,3})$/);
  if (/громкост/i.test(label) && number) {
    const v = Number(number[1]);
    // На станции громкость 0–30, 30 — это 100%
    return {value: v, max: 30, text: `${Math.round(Math.min(v, 30) / 30 * 100)}%`};
  }
  if (/ярк/i.test(label) && percent) {
    const v = Number(percent[1]);
    return {value: v, max: 100, text: `${v}%`};
  }
  if (/ярк/i.test(label) && number) {
    const v = Number(number[1]);
    const max = /led|панел/i.test(card ?? "") || v > 100 ? 255 : 100;
    return {value: v, max, text: max === 100 ? `${v}%` : `${v} из ${max}`};
  }
  return null;
};

export const Scale = ({value, max, text}: { value: number; max: number; text: string }) => {
  const share = Math.max(0, Math.min(1, max ? value / max : 0));
  const filled = Math.round(share * 10);
  return (
    <span className="station-value__scale" title={text}>
      <span className="station-value__bars" aria-hidden="true">
        {Array.from({length: 10}, (_, i) => <i key={i} className={i < filled ? "is-on" : undefined}/>)}
      </span>
      <span className="station-value__scale-text">{text}</span>
    </span>
  );
};

export const Badge = ({tone, children}: { tone: Tone; children: string }) =>
  <span className={`station-value__badge station-value__badge--${tone}`}>{children}</span>;

// Срок в днях: «20 дн.» — зелёный, меньше 30 — жёлтый, 0 и меньше — красный
const daysTone = (days: number): Tone => (days <= 0 ? "bad" : days < 30 ? "warn" : "on");

const DaysText = ({value}: { value: string }) => {
  const parts = value.split(/(-?\d+\s*дн\.)/);
  return (
    <span className="station-value__days">
      {parts.map((part, i) => {
        const days = part.match(/^(-?\d+)\s*дн\.$/);
        return days ? <Badge key={i} tone={daysTone(Number(days[1]))}>{part}</Badge> : part ? <span key={i}>{part}</span> : null;
      })}
    </span>
  );
};

const StationValue = ({label, value, card}: StationValueProps) => {
  const text = value.trim();
  if (!text || EMPTY.test(text)) return <span className="station-value__empty">{text || "—"}</span>;

  const scale = scaleOf(label, text, card);
  if (scale) return <Scale {...scale}/>;

  // «вкл/выкл» — короткой плашкой, полное слово остаётся в подсказке
  if (text.length <= 40 && !/[,;]/.test(text)) {
    // Блокировка администратора выключена — блок или механизм не работает: красная плашка
    if (OFF.test(text)) return <Badge tone={ADMIN_BLOCK.test(label) ? "bad" : "off"}>{text}</Badge>;
    if (ON.test(text)) return <Badge tone="on">{text}</Badge>;
  }

  if (/\d+\s*дн\./.test(text) && /гарант|то\b|обслуж|демо/i.test(label + " " + text)) return <DaysText value={text}/>;

  // Время «05:23», координаты и числа — не переносим посреди
  if (/^[\d\s.,:°NSEW+\-–→]+$/i.test(text) || /^UTC/i.test(text)) return <span className="station-value__nowrap">{text}</span>;

  return <span className="station-value__text">{text}</span>;
};

export default StationValue;
